import { IconNameType } from '@servicetitan/design-system';
import { OutboundCallEntity } from '@lib/types/common-call-types';

export type TaskDirection = 'inbound' | 'outbound';

export type TaskCustomer =
  | {
      id: string;
      name: string;
      /** The customer's tenant-level OrgNode Guid, when the picker knows it. */
      orgNodeId?: string;
      /** The customer's numeric tenant id, when the picker knows it. */
      tenantId?: string;
    }
  | undefined;

export enum AcceptedOnDevice {
  ThisDevice = 'thisDevice',
  OtherDevice = 'otherDevice',
}

export enum TaskState {
  Pending = 'PENDING',
  Offered = 'OFFERED',
  Rejected = 'REJECTED',
  Abandoned = 'ABANDONED',
  Accepted = 'ACCEPTED',
  WrapUp = 'WRAPUP',
  Completed = 'COMPLETED',
  TimedOut = 'TIMEDOUT',
}

export interface TaskOptions {
  id: string;
  tenantId?: string;
  from: string;
  to?: string;
  startedAt?: number;
  type: string;
  direction?: TaskDirection;
  state?: TaskState;
  displayName?: string;
  attributes?: Record<string, any>;
  recoveryStartedAt?: number | null;
  recoveryEndsAt?: number | null;
  queueName?: string;
  queueId?: string;
  isInternal?: boolean;
  isStateActive?: boolean;
  viewKey?: string;
}

/**
 * The selection requests that arrived while a Close prompt held selection.
 * `target` is the latest; `fallbacks` are earlier ones, oldest first, used
 * when `target`'s task is gone by the time it applies. A target of undefined
 * means home.
 */
export type DeferredSelection = {
  target: string | undefined;
  fallbacks?: (string | undefined)[];
};

/**
 * Extra context for `actionTask`. `deferredSelection` holds the selection
 * requests that arrived while a Close prompt was open. `complete` applies the
 * newest one whose task still exists, after its own selection restore.
 */
export interface TaskActionOptions {
  deferredSelection?: DeferredSelection;
  /**
   * The classification requirement a Close pre-check already fetched. `complete`
   * uses it instead of asking again, so a guarded Close makes one request.
   */
  classificationRequirement?: ClassificationRequirement;
  /**
   * View key of the card the CSR was on when they clicked Close. When the closed
   * card is still selected on completion, selection returns to it if it still
   * exists, so overlapping Closes each have their own return card.
   */
  returnViewKey?: string;
}

export interface TransferStatus {
  inProgress: boolean;
  agentId: string;
  initiatedAt: Date;
  transferType: OutboundCallEntity;
  queueId?: string;
  queueName?: string;
}

export type CreateTaskOptions = TaskOptions;

export interface ITask {
  readonly id: string;
  /** Stable across id re-keys; absent on tasks that predate it. */
  readonly viewKey?: string;
  readonly state: TaskState;
  readonly startedAt: Date;
  readonly type: string;
  readonly direction: TaskDirection;
  readonly from: string;
  readonly to?: string;
  readonly tenantId?: string;
  readonly displayName: string;
  readonly attributes: Record<string, any>;
  readonly recoveryStartedAt: number | null;
  readonly recoveryEndsAt: number | null;
  readonly queueName: string | undefined;
  readonly queueId?: string;
  readonly isInternal: boolean;
  readonly isStateActive: boolean;
  readonly loading: boolean;
}

export enum SkipClassificationReason {
  ClassificationCompleted = 'ClassificationCompleted',
  NotRequired = 'NotRequired',
  NoTenantAccess = 'NoTenantAccess',
}

/** A non-null answer from `TasksModule.checkClassificationRequirement`. */
export type ClassificationRequirement =
  | SkipClassificationReason
  | 'NotReady'
  | 'NotEnded'
  | true;

export type HandleTaskHydrationFunction = (
  parsedTask: any,
  hydrateFn: (parsedTask: any) => ITask,
) => ITask | null;

export type TaskTypeDefinition = {
  key: string;
  category: string;
  categoryIcon?: IconNameType;
  types: string[];
  taskViewComponent: React.ComponentType<any>;
  taskRowComponent?: React.ComponentType<any>;
  taskSectionComponent?: React.ComponentType<any>;
  handleHydration?: HandleTaskHydrationFunction;
};
